import React, { useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Card, Chip, ErrorBanner, Field, Loader, PrimaryButton, SectionLabel, TextButton, Toggle, screen } from '../ui';

// ASCII only — ids ride in an idempotency HTTP header (no Cyrillic allowed).
const slug = (text) => (text || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '').slice(0, 30) || 'x';
const num = (v, d) => { const p = parseInt(String(v).trim(), 10); return Number.isFinite(p) ? p : d; };
const PHASE_LABEL = { opening: 'Открытие', active: 'Смена', closing: 'Закрытие' };

export default function LocationsScreen() {
  const { user } = useAuth();
  const locations = useCollection('locations');
  const shiftTypes = useCollection('shiftTypes');
  const checklists = useCollection('checklistTemplates');
  const client = useMemo(() => createAdminClient(user), [user]);

  const [sheet, setSheet] = useState(null); // 'location' | 'shiftType'
  const [detail, setDetail] = useState(null); // location being configured
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const toggle = async (kind, doc) => {
    setBusyId(doc.id);
    setError(null);
    try {
      const fn = kind === 'location' ? client.updateLocation : client.updateShiftType;
      await fn(doc.id, { active: !doc.active });
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (locations.loading && !locations.docs.length) return <Loader />;

  return (
    <View style={screen.root}>
      <ScrollView contentContainerStyle={screen.content}>
        <ErrorBanner message={error || locations.error} />

        <View style={styles.head}>
          <SectionLabel>Точки</SectionLabel>
          <TextButton title="+ Точка" onPress={() => setSheet('location')} />
        </View>
        {locations.docs.map((l) => (
          <Card key={l.id} style={styles.locCard}>
            <View style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.name}>{l.name}</Text>
                <Text style={styles.meta}>{l.timezone} · {l.type}{l.active ? '' : ' · отключена'}</Text>
              </View>
              <Toggle value={!!l.active} onValueChange={() => toggle('location', l)} disabled={busyId === l.id} />
            </View>
            <Pressable onPress={() => setDetail(l)} style={styles.locConfig}>
              <Text style={styles.locConfigText}>
                Должности: {(l.positions || []).length ? l.positions.join(', ') : '—'}
              </Text>
              <Text style={styles.locConfigArrow}>Настроить ›</Text>
            </Pressable>
          </Card>
        ))}

        <View style={[styles.head, { marginTop: spacing.md }]}>
          <SectionLabel>Типы смен</SectionLabel>
          <TextButton title="+ Тип" onPress={() => setSheet('shiftType')} />
        </View>
        {shiftTypes.docs.map((t) => {
          const count = [...(t.openingTemplateIds || []), ...(t.activeTemplateIds || []), ...(t.closingTemplateIds || [])].length;
          return (
            <Card key={t.id} style={styles.row}>
              <View style={{ flex: 1 }}>
                <Text style={styles.name}>{t.name}</Text>
                <Text style={styles.meta}>
                  {t.defaultDurationMinutes ? `${+(t.defaultDurationMinutes / 60).toFixed(1)} ч · ` : ''}
                  {t.wakeUpRequired ? 'с подъёмом' : 'без подъёма'} · {count} чек-листов
                  {t.active ? '' : ' · отключён'}
                </Text>
              </View>
              <Toggle value={!!t.active} onValueChange={() => toggle('shiftType', t)} disabled={busyId === t.id} />
            </Card>
          );
        })}
      </ScrollView>

      {sheet === 'location' && (
        <LocationSheet
          onClose={() => setSheet(null)}
          onSubmit={(id, data) => client.createLocation(id, data)}
        />
      )}
      {detail && (
        <LocationDetailSheet
          location={detail}
          shiftTypes={shiftTypes.docs.filter((t) => t.active)}
          onClose={() => setDetail(null)}
          onSave={async (patch) => {
            try {
              await client.updateLocation(detail.id, patch);
              setDetail(null);
            } catch (e) {
              setError(readableMobileError(e));
            }
          }}
        />
      )}
      {sheet === 'shiftType' && (
        <ShiftTypeSheet
          checklists={checklists.docs.filter((c) => c.active)}
          onClose={() => setSheet(null)}
          onSubmit={(id, data) => client.createShiftType(id, data)}
        />
      )}
    </View>
  );
}

function Sheet({ title, onClose, children }) {
  return (
    <Modal visible animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.sheetHead}>
            <Text style={styles.sheetTitle}>{title}</Text>
            <TextButton title="Отмена" kind="muted" onPress={onClose} />
          </View>
          <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">{children}</ScrollView>
        </View>
      </View>
    </Modal>
  );
}

function LocationSheet({ onClose, onSubmit }) {
  const [f, setF] = useState({ name: '', type: 'retail', timezone: 'Europe/Moscow', open: '08:00', close: '22:00', lead: '30', closingRequired: true, positions: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const submit = async () => {
    if (!f.name.trim()) { setError('Введите название'); return; }
    setBusy(true); setError(null);
    try {
      await onSubmit(`loc-${slug(f.name)}-${Date.now().toString(36)}`, {
        name: f.name.trim(), type: f.type.trim() || 'retail', active: true, timezone: f.timezone.trim(),
        positions: f.positions.split(',').map((p) => p.trim()).filter(Boolean),
        operatingHours: { open: f.open, close: f.close },
        wakeUpPolicy: { requestLeadMinutes: num(f.lead, 30), deadlineMinutes: 10, reminderIntervalMinutes: 15 },
        openingPolicy: {}, closingPolicy: { required: f.closingRequired }, notificationPolicy: { owner: true },
      });
      onClose();
    } catch (e) {
      setError(e?.code ? `Отклонено: ${e.code}` : 'Не удалось создать');
    } finally { setBusy(false); }
  };

  return (
    <Sheet title="Новая точка" onClose={onClose}>
      <ErrorBanner message={error} />
      <Field label="Название" value={f.name} onChangeText={(name) => set({ name })} placeholder="Кофейня на Тверской" />
      <View style={styles.rowFields}>
        <View style={{ flex: 1 }}><Field label="Тип" value={f.type} onChangeText={(type) => set({ type })} /></View>
        <View style={{ flex: 1 }}><Field label="Таймзона" value={f.timezone} onChangeText={(timezone) => set({ timezone })} autoCapitalize="none" /></View>
      </View>
      <View style={styles.rowFields}>
        <View style={{ flex: 1 }}><Field label="Открытие" value={f.open} onChangeText={(open) => set({ open })} /></View>
        <View style={{ flex: 1 }}><Field label="Закрытие" value={f.close} onChangeText={(close) => set({ close })} /></View>
      </View>
      <Field label="Будильник за, мин до старта" value={f.lead} onChangeText={(lead) => set({ lead })} keyboardType="number-pad" />
      <Field label="Должности через запятую" value={f.positions} onChangeText={(positions) => set({ positions })} placeholder="Бариста, Помощник баристы" />
      <Pressable onPress={() => set({ closingRequired: !f.closingRequired })} style={styles.toggleRow}>
        <Text style={styles.toggleLabel}>Требовать закрытие смены</Text>
        <Toggle value={f.closingRequired} onValueChange={(v) => set({ closingRequired: v })} />
      </Pressable>
      <PrimaryButton title="Создать точку" onPress={submit} loading={busy} />
    </Sheet>
  );
}

const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

function LocationDetailSheet({ location, shiftTypes, onClose, onSave }) {
  const [positions, setPositions] = useState(() => [...(location.positions || [])]);
  const [templates, setTemplates] = useState(() => (location.shiftTemplates || []).map((t) => ({ ...t })));
  const [defaultShiftTypeId, setDefaultShiftTypeId] = useState(location.defaultShiftTypeId || '');
  const [geo, setGeo] = useState(() => ({
    lat: location.geo?.lat != null ? String(location.geo.lat) : '',
    lng: location.geo?.lng != null ? String(location.geo.lng) : '',
    radiusM: location.geo?.radiusM != null ? String(location.geo.radiusM) : '300',
  }));
  const setG = (p) => setGeo((x) => ({ ...x, ...p }));
  const [posDraft, setPosDraft] = useState('');
  const [tpl, setTpl] = useState({ label: '', start: '08:00', end: '16:00' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const setT = (p) => setTpl((x) => ({ ...x, ...p }));

  const addPos = () => {
    const name = posDraft.trim();
    if (!name || positions.some((p) => p.toLowerCase() === name.toLowerCase())) { setPosDraft(''); return; }
    setPositions((p) => [...p, name]);
    setPosDraft('');
  };
  const addTpl = () => {
    if (!tpl.label.trim()) { setErr('Название шаблона'); return; }
    if (!HHMM_RE.test(tpl.start) || !HHMM_RE.test(tpl.end)) { setErr('Время в формате ЧЧ:ММ'); return; }
    setErr(null);
    setTemplates((list) => [...list, { id: `t${Date.now().toString(36)}`, label: tpl.label.trim(), start: tpl.start, end: tpl.end }]);
    setTpl({ label: '', start: '08:00', end: '16:00' });
  };

  return (
    <Sheet title={location.name} onClose={onClose}>
      <ErrorBanner message={err} />

      <Text style={styles.groupLabel}>ТИП СМЕНЫ ПО УМОЛЧАНИЮ</Text>
      <Text style={styles.hint}>Какой набор чек-листов применяется к сменам этой точки.</Text>
      <View style={styles.chipWrap}>
        {shiftTypes.map((t) => (
          <Pressable key={t.id} onPress={() => setDefaultShiftTypeId(t.id)} style={[styles.posChip, defaultShiftTypeId === t.id && styles.posChipOn]}>
            <Text style={[styles.posChipText, defaultShiftTypeId === t.id && styles.posChipTextOn]}>{t.name}</Text>
          </Pressable>
        ))}
        {!shiftTypes.length && <Text style={styles.hint}>Сначала создайте тип смены.</Text>}
      </View>

      <Text style={styles.groupLabel}>ДОЛЖНОСТИ</Text>
      <Text style={styles.hint}>Сотрудник выбирает одну, когда ставит себе смену.</Text>
      <View style={styles.chipWrap}>
        {positions.map((p) => (
          <Pressable key={p} onPress={() => setPositions((x) => x.filter((v) => v !== p))} style={styles.posChip}>
            <Text style={styles.posChipText}>{p}</Text><Text style={styles.posChipX}>✕</Text>
          </Pressable>
        ))}
      </View>
      <View style={styles.rowFields}>
        <View style={{ flex: 1 }}><Field label="Новая должность" value={posDraft} onChangeText={setPosDraft} placeholder="Кассир" /></View>
        <View style={styles.addBtnWrap}><TextButton title="+ Добавить" onPress={addPos} /></View>
      </View>

      <Text style={styles.groupLabel}>ШАБЛОНЫ ЧАСОВ</Text>
      <Text style={styles.hint}>Готовые смены на выбор. Сотрудник берёт шаблон и может подвинуть концы.</Text>
      {templates.map((t) => (
        <Pressable key={t.id} onPress={() => setTemplates((list) => list.filter((x) => x.id !== t.id))} style={styles.tplRow}>
          <Text style={styles.tplLabel}>{t.label}</Text>
          <Text style={styles.tplTime}>{t.start}–{t.end}</Text>
          <Text style={styles.posChipX}>✕</Text>
        </Pressable>
      ))}
      <Field label="Название" value={tpl.label} onChangeText={(label) => setT({ label })} placeholder="Утро" />
      <View style={styles.rowFields}>
        <View style={{ flex: 1 }}><Field label="Начало" value={tpl.start} onChangeText={(start) => setT({ start })} placeholder="08:00" /></View>
        <View style={{ flex: 1 }}><Field label="Конец" value={tpl.end} onChangeText={(end) => setT({ end })} placeholder="16:00" /></View>
        <View style={styles.addBtnWrap}><TextButton title="+ Шаблон" onPress={addTpl} /></View>
      </View>

      <Text style={styles.groupLabel}>ГЕО-ОТМЕТКА НА ОТКРЫТИИ</Text>
      <Text style={styles.hint}>
        Координаты точки. При открытии смены приложение запишет, насколько далеко был сотрудник — смену это не блокирует, но видно в отчётах, а если далеко — придёт уведомление. Координаты можно скопировать из Яндекс/Google Карт (правый клик по месту).
      </Text>
      <View style={styles.rowFields}>
        <View style={{ flex: 1 }}><Field label="Широта" value={geo.lat} onChangeText={(lat) => setG({ lat })} placeholder="55.751244" keyboardType="numbers-and-punctuation" autoCapitalize="none" /></View>
        <View style={{ flex: 1 }}><Field label="Долгота" value={geo.lng} onChangeText={(lng) => setG({ lng })} placeholder="37.618423" keyboardType="numbers-and-punctuation" autoCapitalize="none" /></View>
      </View>
      <Field label="Радиус, м" value={geo.radiusM} onChangeText={(radiusM) => setG({ radiusM })} keyboardType="number-pad" />

      <PrimaryButton
        title="Сохранить"
        loading={busy}
        onPress={() => {
          const lat = parseFloat(String(geo.lat).replace(',', '.'));
          const lng = parseFloat(String(geo.lng).replace(',', '.'));
          const hasGeo = geo.lat.trim() !== '' || geo.lng.trim() !== '';
          if (hasGeo && (!Number.isFinite(lat) || Math.abs(lat) > 90 || !Number.isFinite(lng) || Math.abs(lng) > 180)) {
            setErr('Проверьте координаты: широта −90…90, долгота −180…180.');
            return;
          }
          const radiusM = Math.min(5000, Math.max(30, num(geo.radiusM, 300)));
          setErr(null);
          setBusy(true);
          Promise.resolve(onSave({
            positions,
            shiftTemplates: templates,
            defaultShiftTypeId: defaultShiftTypeId || null,
            geo: hasGeo ? { lat, lng, radiusM } : null,
          })).finally(() => setBusy(false));
        }}
      />
    </Sheet>
  );
}

function ShiftTypeSheet({ checklists, onClose, onSubmit }) {
  const [f, setF] = useState({ name: '', durationHours: '10', wakeUpRequired: true });
  const [picked, setPicked] = useState({}); // checklistId -> true
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const submit = async () => {
    if (!f.name.trim()) { setError('Введите название'); return; }
    const buckets = { opening: [], active: [], closing: [] };
    for (const c of checklists) if (picked[c.id]) buckets[c.phase]?.push(c.id);
    setBusy(true); setError(null);
    try {
      await onSubmit(`type-${slug(f.name)}-${Date.now().toString(36)}`, {
        name: f.name.trim(), active: true, defaultDurationMinutes: Math.round(num(f.durationHours, 10) * 60), wakeUpRequired: f.wakeUpRequired,
        openingTemplateIds: buckets.opening, activeTemplateIds: buckets.active, closingTemplateIds: buckets.closing,
      });
      onClose();
    } catch (e) {
      setError(e?.code ? `Отклонено: ${e.code}` : 'Не удалось создать');
    } finally { setBusy(false); }
  };

  return (
    <Sheet title="Новый тип смены" onClose={onClose}>
      <ErrorBanner message={error} />
      <Field label="Название" value={f.name} onChangeText={(name) => set({ name })} placeholder="Кофейня — дневная" />
      <Field label="Длительность, ч" value={f.durationHours} onChangeText={(durationHours) => set({ durationHours })} keyboardType="decimal-pad" />
      <Pressable onPress={() => set({ wakeUpRequired: !f.wakeUpRequired })} style={styles.toggleRow}>
        <Text style={styles.toggleLabel}>Подтверждение подъёма перед сменой</Text>
        <Toggle value={f.wakeUpRequired} onValueChange={(v) => set({ wakeUpRequired: v })} />
      </Pressable>
      <Text style={styles.groupLabel}>ЧЕК-ЛИСТЫ</Text>
      {checklists.length === 0 && <Text style={styles.hint}>Сначала создайте чек-листы на вкладке «Задачи».</Text>}
      {checklists.map((c) => (
        <Pressable key={c.id} onPress={() => setPicked((p) => ({ ...p, [c.id]: !p[c.id] }))} style={[styles.pickRow, picked[c.id] && styles.pickRowOn]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pickName}>{c.name}</Text>
            <Text style={styles.pickMeta}>{PHASE_LABEL[c.phase] || c.phase}</Text>
          </View>
          {picked[c.id] ? <Chip label="выбран" kind="good" /> : null}
        </Pressable>
      ))}
      <PrimaryButton title="Создать тип смены" onPress={submit} loading={busy} />
    </Sheet>
  );
}

const styles = StyleSheet.create({
  head: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  locCard: { gap: spacing.sm },
  locConfig: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: colors.border, paddingTop: spacing.sm, gap: spacing.sm },
  locConfigText: { ...typography.caption, color: colors.textSecondary, flex: 1 },
  locConfigArrow: { ...typography.caption, color: colors.primary, fontWeight: '700' },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  posChip: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.full, paddingHorizontal: spacing.md, paddingVertical: 6 },
  posChipOn: { borderColor: colors.primary, backgroundColor: 'rgba(0,210,160,0.12)' },
  posChipText: { ...typography.caption, color: colors.textPrimary },
  posChipTextOn: { color: colors.primary, fontWeight: '700' },
  posChipX: { ...typography.caption, color: colors.textMuted, fontSize: 11 },
  addBtnWrap: { justifyContent: 'flex-end', paddingBottom: 10 },
  tplRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2 },
  tplLabel: { ...typography.body, color: colors.textPrimary, fontSize: 14, flex: 1 },
  tplTime: { ...typography.caption, color: colors.primary, fontVariant: ['tabular-nums'] },
  name: { ...typography.body, color: colors.textPrimary, fontWeight: '700', fontSize: 14 },
  meta: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '90%' },
  sheetHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  sheetTitle: { ...typography.h2, color: colors.textPrimary },
  rowFields: { flexDirection: 'row', gap: spacing.md },
  groupLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '700', letterSpacing: 1, marginTop: spacing.xs },
  hint: { ...typography.caption, color: colors.textMuted },
  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  toggleLabel: { ...typography.body, color: colors.textPrimary, flex: 1, paddingRight: spacing.md },
  pickRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  pickRowOn: { borderColor: colors.primary },
  pickName: { ...typography.body, color: colors.textPrimary, fontSize: 14 },
  pickMeta: { ...typography.caption, color: colors.textMuted },
});
